import { useMediaQuery }                                                                   from "@mui/material";
import React, { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

export type SidebarContextType = {
  // Desktop behavior (>= md)
  isCollapsed : boolean;
  canToggle   : boolean;
  toggle      : () => void;
  setCollapsed: (collapsed: boolean) => void;
  // Mobile behavior (< md) — consumed by the Drawer in task-301 (wave-2)
  isMobile    : boolean;
  isMobileOpen: boolean;
  openMobile  : () => void;
  closeMobile : () => void;
  toggleMobile: () => void;
};

export const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  const toggle = useCallback(() => setIsCollapsed(prev => !prev), []);
  const setCollapsed = useCallback((collapsed: boolean) => setIsCollapsed(collapsed), []);
  const openMobile = useCallback(() => setIsMobileOpen(true), []);
  const closeMobile = useCallback(() => setIsMobileOpen(false), []);
  const toggleMobile = useCallback(() => setIsMobileOpen(prev => !prev), []);

  // Auto-close mobile drawer when viewport crosses above md.
  useEffect(() => {
    if (!isMobile) setIsMobileOpen(false);
  }, [isMobile]);

  const contextValue = useMemo<SidebarContextType>(() => ({
    isCollapsed: isMobile ? true : isCollapsed,
    canToggle  : !isMobile,
    toggle,
    setCollapsed,
    isMobile,
    isMobileOpen,
    openMobile,
    closeMobile,
    toggleMobile,
  }), [isMobile, isCollapsed, isMobileOpen, toggle, setCollapsed, openMobile, closeMobile, toggleMobile]);

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  );
};
